import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty, transform, upperCase } from "lodash";
import { FiDollarSign } from "react-icons/fi";
import moment from "moment";

import { getPaginatedMissedPayrolls } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import Alert from "components/Alert";
import Paginator from "components/Paginator";
import SimpleModal from "components/SimpleModal";
import InfoTable from "components/InfoTable";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconSpinner from "components/IconSpinner";
import IconMessageCard from "components/IconMessageCard";
import IconTableHeader from "components/IconTableHeader";
import { Col, Form, Row } from "react-bootstrap";
import { adminConstants } from "actions/types";
import DatePicker from "react-datepicker";
import type { RootState } from "types/state";

const isLoadingSelector = createLoadingSelector(
  adminConstants.GET_UNPROCESSED_PAYROLLS
);
const errorSelector = createErrorSelector(
  adminConstants.GET_UNPROCESSED_PAYROLLS
);

/**
 * Runtime shape of `state.admin.missedPayrolls` as this page reads it: the
 * reducer spreads the raw GraphQL payload in, which carries `total`/`results`.
 * The typed slice (`AdminPaginated`) only models `results`/`totalRows`, so we
 * cast at the mapStateToProps boundary. See found-not-fixed note re: `.total`.
 */
interface MissedPayrollsSlice {
  total?: number | null;
  results: unknown[];
}

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    // boundary cast: AdminPaginated lacks `total`, but the reducer spreads the
    // raw GraphQL payload here at runtime (which has `total`).
    missedPayrolls: state.admin
      .missedPayrolls as unknown as MissedPayrollsSlice,
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getPaginatedMissedPayrolls,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface Filters {
  date: string;
  companyId?: string;
}

/** Fields of a missed-payroll row that the table columns render. */
interface MissedPayrollRow extends Record<string, unknown> {
  start?: React.ReactNode;
  end?: React.ReactNode;
  payPeriodStart?: React.ReactNode;
  payPeriodEnd?: React.ReactNode;
}

interface State {
  initialFetching: boolean;
  metadata: Record<string, unknown>;
  page: number;
  limit: number;
  offset: number;
  totalRows: number;
  // initialized to [] but never read; component reads this.props.missedPayrolls
  missedPayrolls: unknown[];
  filters: Filters;
  // written by _getPaginatedMissedPayrolls / its catch, never in initial state
  fetching?: boolean;
  // written by the catch handler, never in initial state
  error?: string;
  // toggled by the metadata modal, never in initial state
  showMetadataModal?: boolean;
}

class Payrolls extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    let filters: Filters = {
      date: moment().utc().format("YYYY-MM-DD"),
    };
    if (!isEmpty(props.companyId)) {
      filters = {
        ...filters,
        companyId: props.companyId,
      };
    }

    this.state = {
      initialFetching: true,
      metadata: {},
      page: 0,
      limit: 25,
      offset: 0,
      totalRows: 0,
      missedPayrolls: [],
      filters,
    };
  }

  componentDidMount() {
    this._getPaginatedMissedPayrolls();
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedMissedPayrolls()
    );
  };

  _getPaginatedMissedPayrolls = () => {
    this.setState({ fetching: true }, () => {
      this.props
        .getPaginatedMissedPayrolls(this.props.client as ApolloClient<object>, {
          limit: this.state.limit,
          offset: this.state.offset,
          ...this.state.filters,
        })
        .then(() => {
          this.setState({
            initialFetching: false,
          });
        })
        .catch((ex: Error) => {
          this.setState({
            fetching: false,
            initialFetching: false,
            error: ex.message,
          });
        });
    });
  };

  _buildPayrollColumns = (): Array<IconTableColumn<MissedPayrollRow>> => {
    const columns: Array<IconTableColumn<MissedPayrollRow>> = [
      {
        label: "Pay Period Id",
        key: "payPeriodId",
      },
      {
        label: "Employer Id",
        key: "entity.id",
      },
      {
        label: "Pay Period State",
        key: "periodState",
      },
      {
        label: "Schedule Period",
        customComponent: (props) => {
          return (
            <div>
              {props.start} - {props.end}
            </div>
          );
        },
      },
      {
        label: "Pay Period",
        customComponent: (props) => {
          return (
            <div>
              {props.payPeriodStart} - {props.payPeriodEnd}
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _handleCompanyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        filters: { ...this.state.filters, companyId: e.target.value },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedMissedPayrolls()
    );
  };

  _handleDateChange = (dateValue: Date | null) => {
    const date = isEmpty(dateValue) ? moment() : moment(dateValue);
    this.setState(
      {
        filters: {
          ...this.state.filters,
          date: date.utc().format("YYYY-MM-DD"),
        },
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedMissedPayrolls()
    );
  };

  _buildMetadataTable = () => {
    const metadata = transform(
      this.state.metadata,
      (acc: Array<{ key: string; label: string }>, val, key) => {
        const labelObject = {
          key,
          label: upperCase(key),
        };
        acc.push(labelObject);
        return acc;
      },
      [] as Array<{ key: string; label: string }>
    );
    return <InfoTable metadata={metadata} data={this.state.metadata} />;
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }

    const columns = this._buildPayrollColumns();
    const data = this.props.missedPayrolls.results as MissedPayrollRow[];
    const showBlankSlate = isEmpty(data);

    return (
      <div className="company-payrolls">
        <Row>
          {isEmpty(this.props.companyId) && (
            <Col md={4}>
              <div
                style={{ paddingLeft: 15, paddingBottom: 15 }}
                className="company-id-filter"
              >
                <Form.Label>Company Id</Form.Label>
                <Form.Control
                  as="input"
                  name="companyIdFilter"
                  value={this.state.filters.companyId}
                  onChange={this._handleCompanyIdChange}
                />
              </div>
            </Col>
          )}
          <Col md={4}>
            <div
              style={{ paddingLeft: 15, paddingBottom: 15 }}
              className="date-filter"
            >
              <Form.Label className="d-block">Schedule End Date</Form.Label>
              <DatePicker
                className="form-control"
                name="anchorPayDate"
                // found-not-fixed: react-datepicker `value` expects a string but
                // this passes a Date; `selected` already drives the picker, so
                // `value` is effectively redundant. Preserved as-is via cast.
                value={
                  moment(this.state.filters.date).toDate() as unknown as string
                }
                dateFormat="MM/dd/yyyy"
                selected={moment(this.state.filters.date).toDate()}
                onChange={(val: Date | null) => {
                  this._handleDateChange(val);
                }}
              />
            </div>
          </Col>
        </Row>

        {showBlankSlate && (
          <div>
            <IconMessageCard
              header={"No Missing Payrolls"}
              subheader="There are currently no missing pay periods to display."
              icon={<FiDollarSign color="white" stroke="#60A4BF" size={16} />}
            />
          </div>
        )}
        {this.props.isFetching && <IconSpinner centered />}
        {!showBlankSlate && (
          <>
            {this.state.showMetadataModal && (
              <SimpleModal
                show={true}
                size={"lg"}
                onClose={() =>
                  this.setState({ showMetadataModal: false, metadata: {} })
                }
                title="Payroll Metadata"
                buttonText="close"
              >
                {this._buildMetadataTable()}
              </SimpleModal>
            )}

            <div className="action-box">
              <IconTableHeader
                tableHeader="Payrolls"
                tableCount={
                  this.props.missedPayrolls.total as number | undefined
                }
              />
              <IconTable columns={columns} data={data} />
              <Paginator
                onChange={this._onPageChange}
                // found-not-fixed: `total` is `number | null` at runtime; when
                // null this divides to 0 (null/limit === 0). Preserved verbatim
                // via the coercion cast rather than guarding it.
                pageCount={
                  (this.props.missedPayrolls.total as unknown as number) /
                  this.state.limit
                }
                page={this.state.page}
              />
            </div>
          </>
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(Payrolls));
